import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  connectionConfigSchema,
  dataSourceConnectionTypeSchema,
  parseConfigSchema,
  pullConfigSchema,
} from "@erp/shared-types";

export const DataSourceSchema = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
  title: z.string(),
  isSource: z.boolean(),
  lastRead: z.string().datetime().nullable(),
  isSink: z.boolean(),
  lastWrite: z.string().datetime().nullable(),
  connectionType: dataSourceConnectionTypeSchema.nullable(),
  connectionConfig: connectionConfigSchema.nullable(),
  /**
   * Credential used to READ this connection. Read and write are separate so a
   * connection that is both a source and a sink needs no single key carrying
   * both capabilities.
   */
  credentialId: z.string().uuid().nullable(),
  /** Credential used to WRITE to this connection; null means it cannot be written to. */
  writeCredentialId: z.string().uuid().nullable(),
});

export const DataSourceGroupSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  dataSources: z.array(DataSourceSchema),
});

export const ListDataSourcesResponseSchema = z.object({
  groups: z.array(DataSourceGroupSchema),
});

export const CreateDataSourceInputSchema = z.object({
  title: z.string().min(1),
  groupId: z.string().uuid().optional(),
  newGroupTitle: z.string().min(1).optional(),
  isSource: z.boolean(),
  isSink: z.boolean(),
  // connectionType is derived from connectionConfig.type by the service.
  connectionConfig: connectionConfigSchema.nullish(),
  credentialId: z.string().uuid().nullish(),
  writeCredentialId: z.string().uuid().nullish(),
});

export const CreateDataSourceResponseSchema = z.object({
  dataSource: DataSourceSchema,
  createdGroup: DataSourceGroupSchema.omit({ dataSources: true }).nullable(),
});

export const UpdateDataSourceGroupInputSchema = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
});

export const UpdateDataSourceInputSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1),
  groupId: z.string().uuid().optional(),
  newGroupTitle: z.string().min(1).optional(),
  isSource: z.boolean(),
  isSink: z.boolean(),
  // connectionType is derived from connectionConfig.type by the service.
  connectionConfig: connectionConfigSchema.nullish(),
  credentialId: z.string().uuid().nullish(),
  writeCredentialId: z.string().uuid().nullish(),
});

export const UpdateDataSourceResponseSchema = z.object({
  dataSource: DataSourceSchema,
  createdGroup: DataSourceGroupSchema.omit({ dataSources: true }).nullable(),
});

export const DeleteDataSourceInputSchema = z.object({
  id: z.string().uuid(),
});

export const CreateDataSourceGroupInputSchema = z.object({
  title: z.string().min(1),
});

export const DeleteDataSourceGroupInputSchema = z.object({
  id: z.string().uuid(),
});

export const CreateDataSourceGroupResponseSchema = z.object({
  group: DataSourceGroupSchema.omit({ dataSources: true }),
});

export const DataSourceLinkSchema = z.object({
  id: z.string().uuid(),
  dataSourceId: z.string().uuid(),
  schemaId: z.string().uuid(),
  schemaName: z.string(),
  foreignSchemaName: z.string(),
  foreignTableName: z.string(),
  // The owning DataSource's connection type — lets pickers filter links by
  // connection kind (e.g. the Blob Storage adapter only offers blob-backed links).
  connectionType: dataSourceConnectionTypeSchema.nullable(),
  pullConfig: pullConfigSchema.nullable(),
  parseConfig: parseConfigSchema.nullable(),
});

export const ListDataSourceLinksResponseSchema = z.object({
  links: z.array(DataSourceLinkSchema),
});

// Result of syncing a link's ingest-table indexes to the schema's per-field
// index config (created missing indexes, dropped no-longer-expected ones).
export const SyncLinkIndexesResponseSchema = z.object({
  createdIndexes: z.array(z.string()),
  droppedIndexes: z.array(z.string()),
});

export const DataSourceLinkGroupSchema = z.object({
  sourceTitle: z.string(),
  links: z.array(DataSourceLinkSchema),
});

export const ListAllDataSourceLinksResponseSchema = z.object({
  groups: z.array(DataSourceLinkGroupSchema),
});

// A DataSource Link's foreignSchemaName / foreignTableName are lowercased and
// double-quoted into `CREATE TABLE "schema"."table"` DDL by the ingest layer.
// Postgres identifiers are capped at 63 bytes and start with a letter or
// underscore, so we require a plain unquoted identifier: this keeps the name
// legal and injection-safe.
export const MAX_FOREIGN_NAME_LENGTH = 63;
const FOREIGN_NAME_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

/** Whether `name` is a valid DataSource Link schema/table name. */
export function isValidForeignName(name: string): boolean {
  return (
    name.length > 0 &&
    name.length <= MAX_FOREIGN_NAME_LENGTH &&
    FOREIGN_NAME_PATTERN.test(name)
  );
}

const ForeignNameSchema = z
  .string()
  .min(1)
  .max(MAX_FOREIGN_NAME_LENGTH)
  .regex(FOREIGN_NAME_PATTERN, {
    message:
      "must start with a letter or underscore and contain only letters, numbers, and underscores",
  });

export const CreateDataSourceLinkInputSchema = z.object({
  dataSourceId: z.string().uuid(),
  schemaId: z.string().uuid(),
  foreignSchemaName: ForeignNameSchema,
  foreignTableName: ForeignNameSchema,
  pullConfig: pullConfigSchema.nullish(),
  parseConfig: parseConfigSchema.nullish(),
});

export const UpdateDataSourceLinkInputSchema = z.object({
  id: z.string().uuid(),
  schemaId: z.string().uuid(),
  foreignSchemaName: ForeignNameSchema,
  foreignTableName: ForeignNameSchema,
  pullConfig: pullConfigSchema.nullish(),
  parseConfig: parseConfigSchema.nullish(),
});

export const DeleteDataSourceLinkInputSchema = z.object({
  id: z.string().uuid(),
});

// Scopes of DataSource Link state validation — see the backend's
// DataSourceStateValidator for what each scope checks.
export const LinkValidationScopeSchema = z.enum([
  "schema",
  "link_config",
  "ingest_table",
  "mappings_vs_table",
  "fdw_mirror",
]);

export const LinkValidationIssueSchema = z.object({
  scope: LinkValidationScopeSchema,
  severity: z.enum(["error", "warning"]),
  // Stable machine-readable code, e.g. "missing_table", "type_mismatch",
  // "fdw_table_missing". The UI keys repair actions off these.
  code: z.string(),
  message: z.string(),
  columnName: z.string().optional(),
  // Human-readable expected/actual values for mismatch-style issues
  // (e.g. "NOT NULL" vs "nullable", or the expected vs actual default).
  expected: z.string().optional(),
  actual: z.string().nullable().optional(),
});

export const ValidateDataSourceLinkResponseSchema = z.object({
  // "invalid" iff any issue has severity "error"; warnings alone stay "ok".
  status: z.enum(["ok", "invalid"]),
  issues: z.array(LinkValidationIssueSchema),
  // Scopes that could not run (e.g. fdw_mirror when FDW isn't configured).
  skippedScopes: z.array(LinkValidationScopeSchema),
});

export const dataSourcesContracts = {
  list: oc
    .input(z.object({}))
    .output(ListDataSourcesResponseSchema)
    .route({ method: "GET" }),
  listLinks: oc
    .input(z.object({ dataSourceId: z.string().uuid() }))
    .output(ListDataSourceLinksResponseSchema)
    .route({ method: "GET" }),
  listAllLinks: oc
    .input(z.object({}))
    .output(ListAllDataSourceLinksResponseSchema)
    .route({ method: "GET" }),
  createLink: oc
    .input(CreateDataSourceLinkInputSchema)
    .output(z.object({ link: DataSourceLinkSchema }))
    .route({ method: "POST" }),
  updateLink: oc
    .input(UpdateDataSourceLinkInputSchema)
    .output(z.object({ link: DataSourceLinkSchema }))
    .route({ method: "POST" }),
  deleteLink: oc
    .input(DeleteDataSourceLinkInputSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "POST" }),
  createGroup: oc
    .input(CreateDataSourceGroupInputSchema)
    .output(CreateDataSourceGroupResponseSchema)
    .route({ method: "POST" }),
  deleteGroup: oc
    .input(DeleteDataSourceGroupInputSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "POST" }),
  create: oc
    .input(CreateDataSourceInputSchema)
    .output(CreateDataSourceResponseSchema)
    .route({ method: "POST" }),
  update: oc
    .input(UpdateDataSourceInputSchema)
    .output(UpdateDataSourceResponseSchema)
    .route({ method: "POST" }),
  delete: oc
    .input(DeleteDataSourceInputSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "POST" }),
  updateGroup: oc
    .input(UpdateDataSourceGroupInputSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "POST" }),
  validateLink: oc
    .input(z.object({ id: z.string().uuid() }))
    .output(ValidateDataSourceLinkResponseSchema)
    .route({ method: "GET" }),
  createLinkTable: oc
    .input(z.object({ id: z.string().uuid() }))
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "POST" }),
  // (Re)creates the app-DB FDW mirror of the link's ingest table — the repair
  // action for fdw_* validation issues.
  syncLinkFdwMirror: oc
    .input(z.object({ id: z.string().uuid() }))
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "POST" }),
  // Creates/drops indexes on the link's ingest table to match the schema's
  // per-field index config — the repair action for missing_index/extra_index
  // validation issues.
  syncLinkIndexes: oc
    .input(z.object({ id: z.string().uuid() }))
    .output(SyncLinkIndexesResponseSchema)
    .route({ method: "POST" }),
} as const;

export type DataSource = z.infer<typeof DataSourceSchema>;
export type DataSourceGroup = z.infer<typeof DataSourceGroupSchema>;
export type ListDataSourcesResponse = z.infer<
  typeof ListDataSourcesResponseSchema
>;
export type CreateDataSourceInput = z.infer<typeof CreateDataSourceInputSchema>;
export type UpdateDataSourceInput = z.infer<typeof UpdateDataSourceInputSchema>;
export type CreateDataSourceGroupInput = z.infer<
  typeof CreateDataSourceGroupInputSchema
>;
export type DataSourceLink = z.infer<typeof DataSourceLinkSchema>;
export type SyncLinkIndexesResponse = z.infer<
  typeof SyncLinkIndexesResponseSchema
>;
export type DataSourceLinkGroup = z.infer<typeof DataSourceLinkGroupSchema>;
export type ListAllDataSourceLinksResponse = z.infer<
  typeof ListAllDataSourceLinksResponseSchema
>;
export type CreateDataSourceLinkInput = z.infer<
  typeof CreateDataSourceLinkInputSchema
>;
export type UpdateDataSourceLinkInput = z.infer<
  typeof UpdateDataSourceLinkInputSchema
>;
export type LinkValidationScope = z.infer<typeof LinkValidationScopeSchema>;
export type LinkValidationIssue = z.infer<typeof LinkValidationIssueSchema>;
export type ValidateDataSourceLinkResponse = z.infer<
  typeof ValidateDataSourceLinkResponseSchema
>;
